import { Button } from '@edsn/pie-components';
import { useSetAtom } from 'jotai';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import {
  type CreateCprValidationEventRequest,
  type ManagementContactGroupMember,
  useAddCprValidationEvent,
} from '#pie/internal/api/pie-bff';
import { cprGreenVerificationAtom } from '#pie/stores/cprVerificationAtom';
import { typedFormFields } from '#pie/utils/typedFormFields';
import { useToast } from '../toast/ToastContext';

interface VerifyDataModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  validationDate: string;
  cprContacts: ManagementContactGroupMember[];
}

interface FormValues extends CreateCprValidationEventRequest {
  confirmation?: string[];
}

const { FormCheckbox } = typedFormFields<FormValues>();

export const VerifyDataModal = ({
  open,
  onOpenChange,
  organisationId,
  validationDate,
  cprContacts,
}: VerifyDataModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const setVerification = useSetAtom(cprGreenVerificationAtom);

  const noRequiredContacts = cprContacts
    ? cprContacts
        .filter(
          (contact) =>
            contact.contactGroup.isRequired &&
            (contact.contactId === null || contact.contactId === undefined),
        )
        .map((contact) => contact.contactGroup.name)
    : [];

  if (open && !organisationId) {
    addToast({
      message: t('cpr_management_group.quality_checkbox.error.message'),
      title: t('cpr_management_group.quality_checkbox.error.title'),
      type: 'error',
    });
  }

  const formMethods = useForm<FormValues>({
    mode: 'onChange',
  });

  const { handleSubmit } = formMethods;

  const { mutate, isPending: isPosting } = useAddCprValidationEvent({
    mutation: {
      onError: (error) => {
        addToast({
          message: t(
            'cpr_management_group.quality_checkbox.toast.error.message',
          ),
          title: t('cpr_management_group.quality_checkbox.toast.error.title'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: t(
            'cpr_management_group.quality_checkbox.toast.success.message',
          ),
          title: t('cpr_management_group.quality_checkbox.toast.success.title'),
          type: 'success',
        });

        setVerification(true);
        onOpenChange(false);
      },
    },
  });

  const onSubmit: SubmitHandler<FormValues> = () => {
    if (!organisationId) {
      addToast({
        message: t(
          'cpr_management_group.quality_checkbox.submit.toast.error.organisation.message',
        ),
        title: t('cpr_management_group.quality_checkbox.toast.error.title'),
        type: 'error',
      });
      return;
    }

    if (noRequiredContacts.length > 0) {
      addToast({
        message: (
          <Trans
            i18nKey="cpr_management_group.quality_checkbox.submit.toast.error.required_contacts.message"
            count={noRequiredContacts.length}
            values={{ groups: noRequiredContacts.join(', ') }}
          />
        ),
        title: t('cpr_management_group.quality_checkbox.toast.error.title'),
        type: 'error',
        duration: 10000,
      });
      return;
    }

    try {
      // Note: API expects organizationId (American spelling) while prop uses British spelling (organisationId)
      mutate({
        data: {
          organizationId: organisationId,
          validationDate: new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error('Error calling mutation:', error);
    }
  };

  return (
    <Dialog
      title={t('cpr_management_group.quality_checkbox.dialog.title')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Trans
            i18nKey="cpr_management_group.quality_checkbox.dialog.message"
            values={{ date: validationDate }}
          />
          <Stack gap="lg" className="mt-4">
            <FormCheckbox
              name="confirmation"
              options={[
                {
                  label: t(
                    'cpr_management_group.quality_checkbox.dialog.checkbox.label',
                  ),
                  value: 'confirmation',
                },
              ]}
              rules={{ required: true }}
            />
            <Stack direction="row" gap="md">
              <Button
                className="flex-1"
                action="tertiary"
                emphasis="subtle"
                onClick={() => onOpenChange(false)}
                disabled={isPosting}
              >
                {t('common.button.cancel')}
              </Button>
              <Button
                className="flex-1"
                isLoading={isPosting}
                disabled={isPosting}
                type="submit"
                action="secondary"
                emphasis="bold"
              >
                {t('common.confirm')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Dialog>
  );
};
